import { Accordion, Button } from "../components/rumonai";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../lib/api";
import { Icon, type IconName } from "../components/Icon";
import { LightMotion } from "../components/LightMotion";
import { platformRequest } from "./api";

export function usePlatformRead<T>(path: string) {
  const currentPath = useRef(path);
  currentPath.current = path;
  const mounted = useRef(true);
  const requestId = useRef(0);
  const pending = useRef<AbortController | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState<{
    path: string;
    data?: T;
    error?: unknown;
    loading: boolean
  }>({ path, loading: true });
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; requestId.current++; pending.current?.abort(); };
  }, []);
  const reload = useCallback(() => setRefresh(value => value + 1), []);
  /** Adopt a validated write response for this path, retiring reads started before it was confirmed. */
  const accept = useCallback((data: T): boolean => {
    if (!mounted.current || currentPath.current !== path) return false;
    requestId.current++;
    pending.current?.abort();
    pending.current = null;
    setResult({ path, data, loading: false });
    return true;
  }, [path]);
  useEffect(() => {
    const controller = new AbortController();
    const ticket = ++requestId.current;
    pending.current = controller;
    const current = () => mounted.current && currentPath.current === path
      && requestId.current === ticket && !controller.signal.aborted;
    setResult(previous => ({ path, data: previous.path === path ? previous.data : undefined, loading: true }));
    platformRequest<T>(path, { signal: controller.signal }).then(data => {
      if (current()) setResult({ path, data, loading: false });
    }).catch(error => {
      if (current()) setResult(previous => ({ ...previous, path, error, loading: false }));
    });
    return () => {
      controller.abort();
      if (pending.current === controller) pending.current = null;
    };
  }, [path, refresh]);
  return {
    data: result.path === path ? result.data : undefined,
    error: result.path === path ? result.error : undefined,
    loading: result.path === path ? result.loading : true,
    reload,
    accept,
  };
}

/** Prevent repeat submits; a failed result is never presented as successful. */
export function usePlatformCommand() {
  const lock = useRef(false);
  const mounted = useRef(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState("");
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const run = async (operation: () => Promise<void>) => {
    if (lock.current) return;
    lock.current = true;
    setPending(true); setError(null); setNotice("");
    try { await operation(); }
    catch (failure) { if (mounted.current) setError(failure); }
    finally { lock.current = false; if (mounted.current) setPending(false); }
  };
  return { pending, error, notice, run, setNotice };
}

/**
 * `failed` names what did not get done ("邮件设置没有保存"). The Hub heads every refused admin request with the
 * same generic line, which says nothing about which press failed; the sentence under it is the reason.
 */
export function PlatformError({ error, retry, failed }: { error: unknown; retry?: () => void; failed?: string }) {
  const failure = ApiError.fromUnknown(error);
  return <div className="platform-error" role="alert">
    <Icon name="alert" />
    <div>
      <strong>{failed ?? failure.title}</strong>
      <p>{failure.detail}</p>
      {failure.hint ? <p>{failure.hint}</p> : null}
      {retry ? <Button variant="secondary" onClick={retry}>重试</Button> : null}
    </div>
  </div>;
}

export function PlatformLoading({ label = "正在读取" }: { label?: string }) {
  return <div className="platform-loading" role="status">
    <LightMotion state="working" />
    <span>{label}…</span>
  </div>;
}

export function PlatformDisclosure({ title, children, className }: { title: ReactNode; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState<string[]>([]);
  return <div className={className}><Accordion value={open} onValueChange={setOpen}
    items={[{ value: "details", title, content: children }]} /></div>;
}

export function PlatformEmpty({ title, children, icon = "wall" }: { title: string; children: ReactNode; icon?: IconName }) {
  return <div className="platform-empty">
    <Icon name={icon} size={32} />
    <h2>{title}</h2>
    <div>{children}</div>
  </div>;
}

export function CommandFeedback({ error, notice, failed }: { error: unknown; notice: string; failed?: string }) {
  return <>
    {error ? <PlatformError error={error} failed={failed} /> : null}
    {notice ? (
      <p className="platform-notice" role="status">
        <Icon name="done" />
        {notice}
      </p>
    ) : null}
  </>;
}

export function PageHeading({ eyebrow, title, children, actions }: {
  eyebrow?: string;
  title: string;
  children?: ReactNode;
  actions?: ReactNode
}) {
  return <header className="platform-page-heading">
    <div>
      {eyebrow ? <p className="platform-eyebrow">{eyebrow}</p> : null}
      <h1>{title}</h1>
      {children ? <div className="platform-lead">{children}</div> : null}
    </div>
    {actions ? <div className="platform-heading-actions">{actions}</div> : null}
  </header>;
}
